import React from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { withRouter } from 'react-router-dom';

import { ChevronRightChunky as ChevronRightChunkySvg } from '@eb/eds-iconography';
import { CrossChunky as CrossChunkySvg } from '@eb/eds-iconography';
import { Icon } from '@eb/eds-icon';
import { IconButton } from '@eb/eds-icon-button';

import {
    FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP,
    FILTER_DRAWER_DISPLAY_ORDER,
} from '../../constants';

import FollowedOrganizersFilterButton from './FollowedOrganizersFilterButton';
import FilterButton from './FilterButton';
import { SelectedDate } from './DateFilterSection';
import OrganizationFilterButton from './OrganizationFilterButton';
import DuplicateEventsFilterButton from './DuplicateEventsFilterButton';

import './FilterPanelDrawers.scss';

const getFilterTypeLabel = (filterType) =>
    FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[filterType].label;

const getFilterValueLabel = (filterType, activeFilter, urlParams) => {
    const currentFilter = FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[filterType];
    const activeValues =
        typeof activeFilter === 'string' ? [activeFilter] : [...activeFilter];

    let activeFilterValueDisplay = currentFilter.values
        .filter(({ value }) => activeValues.includes(value))
        .map(({ display }) => display)
        .join(', ');

    //Wasn't able to find filter value and
    //is dates, then custom date range most likely
    if (!activeFilterValueDisplay && filterType === 'dates') {
        activeFilterValueDisplay = <SelectedDate urlParams={urlParams} />;
    }

    return activeFilterValueDisplay;
};

const FilterPanelDrawerContent = ({ activeFilter, filterType, location }) => {
    const filterDisplay = getFilterTypeLabel(filterType);
    const urlParams = queryString.parse(location.search.replace('?', ''));

    return (
        <>
            {activeFilter ? (
                <div>
                    <div className="filter-panel__drawers__item__sub-header">
                        {filterDisplay}
                    </div>
                    <div
                        className="filter-panel__drawers__item__header--active"
                        data-integration="filter-panel__drawers__item__header--active"
                    >
                        {getFilterValueLabel(
                            filterType,
                            activeFilter,
                            urlParams,
                        )}
                    </div>
                </div>
            ) : (
                <div className="filter-panel__drawers__item__header">
                    {filterDisplay}
                </div>
            )}
            {!activeFilter && <Icon type={<ChevronRightChunkySvg />} />}
        </>
    );
};

const EnhancedDrawerContent = withRouter(FilterPanelDrawerContent);

export const FilterPanelDrawer = ({
    activeFilter,
    children,
    filterType,
    handleRemoveFilter,
    isMobile,
    onClick,
}) => {
    const filterDisplay = getFilterTypeLabel(filterType);

    return (
        <>
            <FilterButton
                isActive={!!activeFilter}
                onClick={() => onClick(filterType)}
                value={filterType}
                isMobile={isMobile}
            >
                {children}
            </FilterButton>
            {activeFilter && (
                <div
                    className="filter-panel__drawers__item--close-btn"
                    data-integration="filter-panel__drawers__item--close-btn"
                >
                    <IconButton
                        title={`Remove ${filterDisplay} filter`}
                        iconType={<CrossChunkySvg />}
                        iconColor="ui-blue"
                        onClick={() =>
                            handleRemoveFilter({ [filterType]: true })
                        }
                    />
                </div>
            )}
        </>
    );
};

export const FilterPanelDrawers = (props) => {
    return (
        <ul
            className="filter-panel__drawers"
            data-integration="filter-panel__drawers"
        >
            <>
                <li className="eds-l-pad-top-6 eds-l-pad-bot-5 eds-l-pad-left-6 eds-l-pad-right-6">
                    <FollowedOrganizersFilterButton {...props} />
                </li>
            </>
            {!props.isLoading &&
                props.selectedFilters.organizations.length > 0 && (
                    <OrganizationFilterButton />
                )}
            {!props.isLoading && props.selectedFilters.hash && (
                <DuplicateEventsFilterButton />
            )}
            <div className="eds-l-pad-vert-3 eds-l-pad-left-6 eds-l-pad-right-6">
                <div className="filter-panel__drawers__divider" />
            </div>
            {FILTER_DRAWER_DISPLAY_ORDER.map((filterType, index) => {
                if (filterType === 'divider') {
                    return (
                        <div
                            key={`${
                                FILTER_DRAWER_DISPLAY_ORDER[index - 1]
                            }-divider`}
                            className="eds-l-pad-vert-3 eds-l-pad-left-6 eds-l-pad-right-6"
                        >
                            <div className="filter-panel__drawers__divider" />
                        </div>
                    );
                }

                return (
                    <li
                        key={filterType}
                        className="filter-panel__drawers__item"
                    >
                        <FilterPanelDrawer
                            filterType={filterType}
                            onClick={props.handleDrawerSelect}
                            activeFilter={!!props.selectedFilters[filterType]}
                            handleRemoveFilter={props.handleRemoveFilter}
                            isMobile={props.isMobile}
                        >
                            <EnhancedDrawerContent
                                activeFilter={props.selectedFilters[filterType]}
                                filterType={filterType}
                            />
                        </FilterPanelDrawer>
                    </li>
                );
            })}
        </ul>
    );
};

FilterPanelDrawers.propTypes = {
    selectedFilters: PropTypes.shape({
        tags: PropTypes.string,
        format: PropTypes.string,
        dates: PropTypes.string,
        price: PropTypes.string,
        currencies: PropTypes.string,
        languages: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.arrayOf(PropTypes.string),
        ]),
        followedOrganizers: PropTypes.bool,
        organizations: PropTypes.arrayOf(PropTypes.string),
    }),
    handleDrawerSelect: PropTypes.func,
    handleRemoveFilter: PropTypes.func,
};

export default FilterPanelDrawers;
